import { Navigate, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useAppContext } from '../../store/AppContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const PAYWALL_EXEMPT_PATHS = ['/onboarding', '/paywall', '/premium/success', '/premium/cancel', '/settings'];
const ONBOARDING_EXEMPT_PATHS = ['/onboarding', '/premium/success', '/premium/cancel'];

const Loading = () => (
  <div className="flex min-h-screen items-center justify-center bg-white">
    <p className="text-sm text-gray-500">Loading...</p>
  </div>
);

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const {
    user,
    loading,
    authError,
    isDemoMode,
    onboardingStatus,
    isAdmin,
    entitlementReason,
    retryProfile,
  } = useAppContext();

  const isAdminBypass = entitlementReason === 'admin';
  const location = useLocation();

  const hasLoggedFirstRender = useRef(false);
  useEffect(() => {
    if (hasLoggedFirstRender.current) return;
    hasLoggedFirstRender.current = true;
    console.log('[ProtectedRoute] First render:', {
      isDemoMode, user: user ? 'present' : 'null', loading, pathname: location.pathname,
    });
  }, [location.pathname, user, loading]);

  // ── GATE 1: Demo mode ────────────────────────────────────────────────────────
  if (isDemoMode) return <>{children}</>;

  // ── GATE 2: Auth ─────────────────────────────────────────────────────────────
  if (loading) return <Loading />;
  if (!user || authError) return <Navigate to="/auth/login" replace />;

  // ── GATE 3: Email verification — soft, banner only ───────────────────────────
  // (no action needed here — EmailVerificationBanner handles display)

  // ── GATE 4: Onboarding — single enum, no localStorage ───────────────────────
  const isOnboardingExempt = ONBOARDING_EXEMPT_PATHS.some(p => location.pathname.startsWith(p));

  // Sticky onboarding guard — complete user must not stay on /onboarding
  if (location.pathname.startsWith('/onboarding') && onboardingStatus === 'complete') {
    console.log('[ProtectedRoute] Onboarding already complete — redirecting to /home');
    return <Navigate to="/home" replace />;
  }

  if (!isOnboardingExempt) {
    // loading — backend truth not yet known, never allow through
    if (onboardingStatus === 'loading') return <Loading />;

    // error — profile fetch failed
    if (onboardingStatus === 'error' && !isAdminBypass) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-white px-4">
          <div className="text-center space-y-4">
            <p className="text-sm text-gray-700">Couldn't load your account.</p>
            <p className="text-xs text-gray-500">Please check your connection and try again.</p>
            <button
              onClick={retryProfile}
              className="rounded-lg bg-[#141517] px-6 py-2.5 text-sm font-medium text-white"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    // incomplete — redirect to onboarding
    if (onboardingStatus === 'incomplete') {
      return <Navigate to="/onboarding" replace />;
    }
  }

  // ── GATE 5: Entitlement ──────────────────────────────────────────────────────
  const isPaywallExempt = PAYWALL_EXEMPT_PATHS.some(p => location.pathname.startsWith(p));

  if (!isPaywallExempt) {
    if (isAdmin) {
      console.log('[ProtectedRoute] Admin bypass — skipping subscription gate');
      return <>{children}</>;
    }
  }

  return <>{children}</>;
}
